import type { ISerialize } from '@/objects/ISerialize';
import { Participant } from './Participant';
import { StartingNumber } from './StartingNumber';
import { formatDateIso } from '@/utilities/dateutils';

export class TrackingContestant implements ISerialize {
    public ID: number;

    public EventID: number;

    public FirstStartingTime: Date | null;

    public IgnoreFromTracking: boolean | null;

    public Participant: Participant | null;

    public StartingNumber: StartingNumber | null;

    public StatusID: number;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        EventID?: number;
        FirstStartingTime?: Date | null;
        IgnoreFromTracking?: boolean | null;
        Participant?: Participant | null;
        StartingNumber?: StartingNumber | null;
        StatusID?: number;
    }) {
        this.ID = values.ID ?? 0;

        this.EventID = values.EventID ?? 0;

        this.FirstStartingTime = values.FirstStartingTime ?? null;

        this.IgnoreFromTracking = values.IgnoreFromTracking ?? null;

        this.Participant = values.Participant ?? null;

        this.StartingNumber = values.StartingNumber ?? null;

        this.StatusID = values.StatusID ?? 0;
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"FirstStartingTime": ${this.FirstStartingTime ? '"' + formatDateIso(this.FirstStartingTime) + '"' : null}` +
            ',' +
            `"IgnoreFromTracking": ${JSON.stringify(this.IgnoreFromTracking)}` +
            ',' +
            `"Participant": ${!this.Participant ? null : this.Participant?.serialize()}` +
            ',' +
            `"StartingNumber": ${!this.StartingNumber ? null : this.StartingNumber?.serialize()}` +
            ',' +
            `"StatusID": ${JSON.stringify(this.StatusID)}` +
            '}'
        );
    }

    /**
     * Creates an instance of TrackingContestant from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        EventID?: number;
        FirstStartingTime?: Date | null;
        IgnoreFromTracking?: boolean | null;
        Participant?: Participant | null;
        StartingNumber?: StartingNumber | null;
        StatusID?: number;
    }): TrackingContestant {
        return new TrackingContestant(values);
    }

    public static _FromServerJson(json: any): TrackingContestant {
        return new TrackingContestant({
            ID: json.ID,
            EventID: json.EventID,
            FirstStartingTime: json.FirstStartingTime
                ? json.FirstStartingTime instanceof Date
                    ? json.FirstStartingTime
                    : new Date(json.FirstStartingTime)
                : null,
            IgnoreFromTracking: json.IgnoreFromTracking ?? null,
            Participant: json.Participant ? Participant._FromServerJson(json.Participant) : null,
            StartingNumber: json.StartingNumber ? StartingNumber._FromServerJson(json.StartingNumber) : null,
            StatusID: json.StatusID,
        });
    }

    /**
     * Creates an instance of TrackingContestant from another instance of TrackingContestant.
     * @param other The TrackingContestant you want to clone
     * @returns A cloned TrackingContestant
     */
    public static FromOther(other: TrackingContestant): TrackingContestant {
        return TrackingContestant._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return TrackingContestant.FromOther(this);
    }

    static NAME_OF_CLASS = 'TrackingContestant';
}
